import { getISODateFromDurationString } from '@buildwise/libs/helpers';
import { Autocomplete, Box, FormControl, FormLabel, Grid, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ConfigEntryType } from 'config/config';
import { theme } from 'config/theme';
import moment from 'moment';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CalendarFillIcon from 'remixicon-react/CalendarFillIcon';

type DateFilterFieldProps = {
  field: ConfigEntryType;
};

const durationOptions = [
  { label: 'Custom', value: 'Custom' },
  { label: 'Last 7 days', value: '1W' },
  { label: 'Last 30 days', value: '1M' },
  { label: 'Last 6 months', value: '6M' },
  { label: 'Last 1 Year', value: '1Y' },
];

function DateFilterField({ field }: DateFilterFieldProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
    resetField,
  } = useFormContext<Record<string, any>>();
  const selectedDuration = useWatch({ control, name: 'duration', defaultValue: null });
  const hey = useWatch({ control, name: field.entryCode, defaultValue: null });
  const maxDate = moment().startOf('day').toISOString();

  const getSelectedOption = useMemo(() => {
    return durationOptions?.find((option) => option?.value === selectedDuration);
  }, [selectedDuration]);

  return (
    <Box>
      <Controller
        control={control}
        render={({ field: _field, fieldState }) => {
          return (
            <FormControl fullWidth>
              <Autocomplete
                {..._field}
                value={getSelectedOption || null}
                onChange={(event, value) => {
                  if (value?.value) {
                    setValue('duration', value?.value);
                    if (value?.value && value?.value !== 'Custom') {
                      let gt = new Date(getISODateFromDurationString(value?.value));
                      let lt = new Date(moment().startOf('day').toISOString());
                      setValue(`MIN_${field.entryCode}`, gt);
                      setValue(`MAX_${field.entryCode}`, lt);
                    } else {
                      resetField(`MIN_${field.entryCode}`);
                      resetField(`MAX_${field.entryCode}`);
                      resetField(field.entryCode);
                    }
                  } else {
                    setValue('duration', null);
                    setValue(`MIN_${field.entryCode}`, null);
                    setValue(`MAX_${field.entryCode}`, null);
                    setValue(field.entryCode, null);
                  }
                }}
                options={durationOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!fieldState.error}
                    helperText={fieldState?.error?.message || ''}
                    InputLabelProps={{ shrink: true }}
                    variant="standard"
                    placeholder="Select Duration"
                  />
                )}
                disabled={isSubmitting}
              />
            </FormControl>
          );
        }}
        name={'duration'}
      />

      {selectedDuration === 'Custom' && (
        <Box sx={{ mt: 2 }}>
          <FormLabel sx={{ color: theme.palette.custom.black, fontSize: 14, fontWeight: 700 }}>
            Between
          </FormLabel>
          <Grid container spacing={1.5}>
            <Grid item sm={12} md={6}>
              <Controller
                control={control}
                render={({ field: _field, fieldState }) => (
                  <FormControl fullWidth sx={{ my: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {..._field}
                        value={!!_field.value ? _field.value : null}
                        InputAdornmentProps={{ position: 'end' }}
                        maxDate={maxDate}
                        components={{
                          OpenPickerIcon: () => (
                            <CalendarFillIcon color={theme.palette.custom.black} />
                          ),
                        }}
                        inputFormat=""
                        disableMaskedInput
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            placeholder="Select Date"
                            sx={{
                              '& .MuiInput-input': {
                                padding: '8px 0px',
                                textTransform: 'uppercase',
                              },
                            }}
                          />
                        )}
                        disabled={isSubmitting}
                      />
                    </LocalizationProvider>
                  </FormControl>
                )}
                name={`MIN_${field.entryCode}`}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Controller
                control={control}
                render={({ field: _field, fieldState }) => (
                  <FormControl fullWidth sx={{ my: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {..._field}
                        value={!!_field.value ? _field.value : null}
                        // minDate={startDate || null}
                        InputAdornmentProps={{ position: 'end' }}
                        components={{
                          OpenPickerIcon: () => (
                            <CalendarFillIcon color={theme.palette.custom.black} />
                          ),
                        }}
                        maxDate={maxDate}
                        inputFormat=""
                        disableMaskedInput
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            placeholder="Select Date"
                            sx={{
                              '& .MuiInput-input': {
                                padding: '8px 0px',
                                textTransform: 'uppercase',
                              },
                            }}
                          />
                        )}
                        disabled={isSubmitting}
                      />
                    </LocalizationProvider>
                  </FormControl>
                )}
                name={`MAX_${field.entryCode}`}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default DateFilterField;
